import { FC, useEffect, useState } from 'react';
import { Box, Button, TextField, InputAdornment, IconButton, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNotificationContext } from '../../../shared/hook/useNotificationContext';
import { isDevelopment } from '../../../shared/util/Utilities';
import { DEV_URL_MAP, PROD_URL_MAP, ServiceName } from '../../../api/urlHelper';
import { Modal } from '../../../components/Modal';
import { ChatRequest } from '../../../type/request/ChatRequest';

type RequestCreationConfirmModalProps = {
    onClose: () => void;
    open: boolean;
    requests: ChatRequest[];
};

const RequestCreationConfirmModal: FC<RequestCreationConfirmModalProps> = ({ open, onClose, requests }) => {
    const { setNotification } = useNotificationContext();
    const [requestLink, setRequestLink] = useState<string>('');

    useEffect(() => {
        const request = requests[0];
        if (!request) return;

        const { id: requestId, client } = request;
        const url: string | undefined = isDevelopment
            ? DEV_URL_MAP.get(ServiceName.IAUTO_FRONTEND_CLIENT)
            : PROD_URL_MAP.get(ServiceName.IAUTO_FRONTEND_CLIENT);

        if (requestId && url) {
            const link = client
                ? `${url}/requests/${requestId}`
                : `${url}/promo/requests/${requestId}`;
            setRequestLink(link);
        }
    }, [requests]);

    const onCopyLink = async (): Promise<void> => {
        try {
            await navigator.clipboard.writeText(requestLink);
            setNotification({ type: 'success', message: 'Request URL copied successfully!' }, 2000);
        } catch (error) {
            console.error('Error copying link:', error);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Request Created Successfully"
            maxWidth="md"
            actions={
                <Button
                    variant="contained"
                    onClick={onClose}
                    startIcon={<CheckCircleIcon />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    Close
                </Button>
            }
        >
            <Box sx={{ py: 2 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Your request has been created successfully! Share this link with the client:
                </Typography>

                <TextField
                    fullWidth
                    value={requestLink || 'Loading...'}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    onClick={onCopyLink}
                                    disabled={!requestLink}
                                    color="primary"
                                    edge="end"
                                >
                                    <ContentCopyIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'action.hover',
                        },
                    }}
                />
            </Box>
        </Modal>
    );
};

export default RequestCreationConfirmModal;
